import { PlusOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Form, Row, Space, Table } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { perProps } from '../../../common/types/commonTypes';
import { debounce, filterNullColumns, rangePresets } from '../../../common/utils/common.utils';
import usePagination from '../../../common/utils/usePagination';
import useUrlParams from '../../../common/utils/useUrlParams';
import BreadCrumb from '../../../components/common/breadCrumb/BreadCrumb';
import { FormInput } from '../../../components/common/FormItem/FormItems';
import Reload from '../../../components/common/reload/Reload';
import { loadingIndicator } from '../../../components/common/spinner/LoadingIndicator';
import { formatDateRangeArray, parseDateRangeObject } from '../../../helpers/healper';
import { useGetAllExpensesQuery } from '../Api/Endpoints/ExpenseEndpoints';
import { ExpenseUtils } from '../Utils/ExpenseUtils';

const ExpenseHistory = ({ permission }: perProps) => {
  const { setUrlParam, setUrlParamsFromObject, getAllUrlParams } = useUrlParams();
  const { page, size, changePagination, pagination } = usePagination();
  const params_value = getAllUrlParams() as {
    search: string | null;
    from_date: string | null;
    to_date: string | null;
  };

  const {
    data,
    refetch: getAllRefetch,
    isLoading: dataIsLoading,
  } = useGetAllExpensesQuery({
    ...getAllUrlParams(),
    page,
    size,
  });
  // Add safety check for data mapping
  const dataWithKey =
    data?.data?.map((item, index) => ({
      key: String(index),
      ...item,
    })) || [];

  const [form] = useForm();

  const handelSearch = useCallback(
    debounce((e: string) => {
      setUrlParam('search', e);
    }, 600),
    []
  );

  useEffect(() => {
    form.setFieldsValue({
      search: params_value.search || '',
      date_range: parseDateRangeObject({
        from_date: params_value.from_date,
        to_date: params_value.to_date,
      }),
    });
  }, [params_value]);

  // TABLE COLUMNS
  const columns = ExpenseUtils({ permission, pagination });
  const filterColumns = filterNullColumns(data?.data, columns);

  return (
    <div>
      <BreadCrumb arrOfOption={['Expense', 'History']} reloaderSize='small' />

      <Row justify={'space-between'}>
        <Col lg={4}>
          <Space style={{ marginBottom: '1rem' }}>
            {permission?.['create:any'] && (
              <Link to='/expense/add' state={location.pathname}>
                <Button type='primary'>
                  <PlusOutlined />
                  Add Expense
                </Button>
              </Link>
            )}
            <Reload
              refetch={() => {
                getAllRefetch();
              }}
            />
          </Space>
        </Col>

        <Col span={12}>
          <Form form={form}>
            <Row justify={'end'} gutter={[10, 10]}>
              <Col xs={24} sm={12} md={8} lg={8}>
                <DatePicker.RangePicker
                  name={'date_range'}
                  presets={rangePresets}
                  format={'YYYY-MM-DD'}
                  onChange={(e: any) => setUrlParamsFromObject(formatDateRangeArray(e))}
                />
              </Col>
              <FormInput
                name={'search'}
                placeholder='Search here ...'
                onChange={(e: string) => handelSearch(e.trim())}
              />
            </Row>
          </Form>
        </Col>
      </Row>

      <div>
        <span style={{ marginLeft: '5px', float: 'left', marginBottom: '1rem' }}>
          You have total <strong>{data?.count || data?.data?.length || 0}</strong> rows.
        </span>
        <Table
          rowKey={(e) => e.expense_id}
          size='small'
          bordered
          columns={filterColumns.col}
          dataSource={dataWithKey}
          scroll={{ x: 'max-content' }}
          loading={{
            spinning: dataIsLoading,
            indicator: loadingIndicator,
          }}
          pagination={changePagination(data?.count)}
        />
      </div>
    </div>
  );
};

export default ExpenseHistory;
